import React, { createContext, ReactNode, useContext, useState } from 'react';
import { GlobalContext } from 'lynkco-up-core';
import { GetColab } from '../../modules/Colab/types';
import { axiosInstance } from '../../services/shared';
import { serviceConfig } from '../../config';

interface ColabContextType {
  data: GetColab[];
  setData: React.Dispatch<React.SetStateAction<GetColab[]>>;
  updateStatus: (partitionKey: string, rowKey: string, newStatus: string) => Promise<void>;
  updateCategory: (partitionKey: string, rowKey: string, newCategory: string) => Promise<void>;
}

const ColabContext = createContext<ColabContextType | undefined>(undefined);

export const useColabContext = (): ColabContextType => {
  const context = useContext(ColabContext);
  if (!context) {
    throw new Error('useStatus must be used within a ColabProvider');
  }
  return context;
};

export const ColabProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { alert } = useContext(GlobalContext);
  const [data, setData] = useState<GetColab[]>([]);

  const updateStatus = async (partitionKey: string, rowKey: string, newStatus: string) => {
    const url = `${serviceConfig.endpoints.legacy.root}${serviceConfig.endpoints.legacy.colab}/record/update`;
    try {
      const payload = JSON.stringify({ partitionKey, rowKey, status: newStatus });
      const response = await axiosInstance.patch(url, payload, { headers: { 'Content-Type': 'application/json' } });

      if (response.status === 200) {
        setData(prevRecord =>
          prevRecord.map(record =>
            record.partitionKey === partitionKey && record.rowKey === rowKey
              ? {
                  ...record,
                  status: newStatus,
                }
              : record,
          ),
        );
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert.show(`Could not update status. Please try again later.`, 'error');
    }
  };

  const updateCategory = async (partitionKey: string, rowKey: string, newCategory: string) => {
    const url = `${serviceConfig.endpoints.legacy.root}${serviceConfig.endpoints.legacy.colab}/record/update`;
    try {
      const payload = JSON.stringify({ partitionKey: partitionKey, rowKey, category: newCategory });
      const response = await axiosInstance.patch(url, payload, { headers: { 'Content-Type': 'application/json' } });

      if (response.status === 200) {
        setData(prevRecord =>
          prevRecord.map(record =>
            record.partitionKey === partitionKey && record.rowKey === rowKey
              ? {
                  ...record,
                  category: newCategory,
                }
              : record,
          ),
        );
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      alert.show(`Could not update category. Please try again later.`, 'error');
    }
  };

  return (
    <ColabContext.Provider value={{ data, setData, updateStatus, updateCategory }}>{children}</ColabContext.Provider>
  );
};
